import { useEffect, useLayoutEffect, useState } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { decodeJwtClaims } from '../jwt'
import { getAccessToken } from '../../storage/auth'
import { thumbnailCache, thumbnailKey } from './thumbnail-cache'
import { loadThumbnail } from './thumbnail-loader'

// The session's organization: the context's claims once they are set, the
// token's until then (a reload starts with empty claims).
export const useOrganizationID = () => {
  const authContext = useAuthContext()
  const fromContext = authContext && authContext.state && authContext.state.organizationID
  if (fromContext) return fromContext
  const claims = decodeJwtClaims(getAccessToken())
  return claims ? claims.organizationID : undefined
}

// The nearest ancestor that scrolls: the grid's own scroll box, so the
// look-ahead margin applies to it (a margin on the viewport doesn't reach
// past an inner scroll box's clip).
const scrollParent = el => {
  for (let parent = el.parentElement; parent && parent !== document.body && parent !== document.documentElement; parent = parent.parentElement) {
    const style = window.getComputedStyle(parent)
    if (/(auto|scroll|overlay)/.test(`${style.overflowY} ${style.overflowX}`)) return parent
  }
  return null
}

// True once the element is within `rootMargin` of what is on screen, and
// from then on. Without IntersectionObserver, at once.
export const useNearViewport = (ref, { rootMargin = '400px' } = {}) => {
  const [near, setNear] = useState(() => typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined')

  useEffect(() => {
    if (near || !ref.current) return undefined
    const observer = new window.IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      { root: scrollParent(ref.current), rootMargin }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [near, ref, rootMargin])

  return near
}

// An object URL for the blob while it is shown; revoked when the blob changes
// or the card goes. Set before the browser paints, so a remembered picture
// never flashes the placeholder.
const useObjectURL = blob => {
  const [url, setURL] = useState()
  useLayoutEffect(() => {
    if (!blob) {
      setURL(undefined)
      return undefined
    }
    const objectURL = URL.createObjectURL(blob)
    setURL(objectURL)
    return () => URL.revokeObjectURL(objectURL)
  }, [blob])
  return url
}

const start = (identity, asset, key) => {
  const blob = key ? thumbnailCache.peek(key) : undefined
  // checked: the page's memory and the browser's storage have been asked.
  return { identity, blob, checked: !asset || !key || !!blob, fromMemory: !!blob }
}

// A node card's thumbnail (content's asset-thumbnail row, or nothing) as an
// object URL to draw, or undefined while there is none:
//   1. this page's memory: at once;
//   2. the browser's storage: as soon as it answers, near the viewport or not;
//   3. the network: once the card (ref) nears the viewport, a few at a time.
export const useThumbnailURL = (asset, ref) => {
  const organizationID = useOrganizationID()
  const key = thumbnailKey(organizationID, asset)
  const identity = asset ? `${asset.id}|${key}` : ''
  const near = useNearViewport(ref)

  const [state, setState] = useState(() => start(identity, asset, key))
  let current = state
  if (state.identity !== identity) {
    current = start(identity, asset, key)
    setState(current)
  }
  const { blob, checked, fromMemory } = current

  useEffect(() => {
    if (fromMemory) thumbnailCache.touch(key)
  }, [fromMemory, key])

  useEffect(() => {
    if (checked) return undefined
    let live = true
    thumbnailCache.get(key).then(found => {
      if (live) setState(s => (s.identity === identity ? { ...s, blob: found || undefined, checked: true } : s))
    })
    return () => {
      live = false
    }
  }, [checked, identity, key])

  useEffect(() => {
    if (!asset || !checked || blob || !near) return undefined
    const controller = new AbortController()
    loadThumbnail(asset, key, { signal: controller.signal })
      .then(found => {
        if (found && !controller.signal.aborted) setState(s => (s.identity === identity ? { ...s, blob: found } : s))
      })
      .catch(() => {})
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, checked, !!blob, near])

  return useObjectURL(blob)
}
